import { useState } from "react";
import { BulkExportButton, useListContext, useNotify, useRefresh, useUnselectAll } from "react-admin";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";

/**
 * Bulk actions for the duplicate claimant review list (#6717).
 *
 * Bulk DISMISS is the whole reason this list exists rather than a task queue:
 * the judgement is comparative, and clearing a batch of $0/$0 same-name
 * dependents should be one action, not fifteen.
 *
 * There is deliberately NO bulk merge. Dismissal changes no financial record; a
 * merge moves money between claimants and needs its own per-pair impact preview
 * (#6519). The symmetry is tempting and wrong.
 *
 * Posts to the dedicated /bulk-dismiss endpoint rather than looping updateMany:
 * the backend skips already-terminal pairs in SQL and reports requested vs
 * dismissed, so the user is told "3 of 5" instead of a blanket success.
 */
export const DuplicateClaimantPairBulkActions = () => {
  const { selectedIds } = useListContext();
  const { canEdit } = useResourcePermissions(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  const unselectAll = useUnselectAll(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleDismiss = async () => {
    setSubmitting(true);
    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/duplicate-claimant-pairs/bulk-dismiss`, {
        method: "POST",
        body: JSON.stringify({ ids: selectedIds, reason }),
      });

      // Report what actually moved. Pairs already merged or self-resolved are
      // skipped server-side, and saying "5 dismissed" when 2 were skipped would
      // be a quiet lie.
      const dismissed = json?.dismissed ?? 0;
      const requested = json?.requested ?? selectedIds.length;
      notify(
        dismissed === requested
          ? `Dismissed ${dismissed} pair${dismissed === 1 ? "" : "s"}`
          : `Dismissed ${dismissed} of ${requested} — the rest were already resolved`,
        { type: dismissed === requested ? "success" : "info" }
      );
      unselectAll();
      refresh();
      setOpen(false);
      setReason("");
    } catch (err) {
      notify("Could not dismiss the selected pairs", { type: "error" });
      console.error("bulk dismiss failed", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BulkExportButton />
      {canEdit && (
        <Button size="small" startIcon={<DoNotDisturbOnIcon />} onClick={() => setOpen(true)}>
          Dismiss
        </Button>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dismiss {selectedIds.length} suspected duplicate{selectedIds.length === 1 ? "" : "s"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            These pairs will be recorded as not the same person and leave the review queue. They will return
            automatically if the underlying evidence changes — for example if a corrected date of birth makes the two
            records match after all.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            placeholder="e.g. confirmed siblings, different people"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleDismiss} variant="contained" disabled={submitting}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
